import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { FaCheck } from "react-icons/fa6";
import SortableTable from "../../../Componentes/Utils/SortableTable";
import { formatCurrency } from "../../../Funcoes/formatCurrency";
import { useModal } from "../../../contexts/ModalContext";
import useApi from "../../../hooks/useApi";
import ModalConciliacaoConta from "./ModalConciliacaoConta";
import ModalSelecionarMes from "./ModalSelecionarMes";

const PAGE_SIZE = 100;
const SCROLL_THRESHOLD = 40;

const PageContent = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 12px;
    box-sizing: border-box;
    overflow: hidden;
`;

const HeaderBar = styled.div`
    width: 100%;
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    align-items: center;
    flex-shrink: 0;
`;

const Badge = styled.div`
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.12);
    color: rgba(255, 255, 255, 0.9);
    padding: 8px 10px;
    border-radius: 10px;
    font-size: 13px;
    white-space: nowrap;
`;

const ActionButton = styled.button`
    height: 40px;
    padding: 0 14px;
    border-radius: 10px;
    border: 1px solid rgba(255, 255, 255, 0.18);
    background: rgba(0, 123, 255, 0.85);
    color: #fff;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;

    &:hover {
        opacity: 0.9;
    }
`;

const FilterInput = styled.input`
    height: 40px;
    flex: 1;
    min-width: 240px;
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.18);
    background: rgba(255, 255, 255, 0.08);
    color: #fff;
    padding: 0 12px;
    box-sizing: border-box;
    outline: none;

    &::placeholder {
        color: rgba(255, 255, 255, 0.7);
    }

    &:focus {
        border-color: rgba(0, 123, 255, 0.9);
        box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
    }
`;

const TableShell = styled.div`
    width: 100%;
    flex: 1;
    min-height: 0;
    background-color: #ffffff;
    border-radius: 15px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
`;

const TableFlexFill = styled.div`
    flex: 1;
    min-height: 0;
    width: 100%;
    display: flex;
    flex-direction: column;

    & > div {
        flex: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;
    }
`;

const CheckCell = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #16a34a;
`;

function formatPeriodoLabel(periodo) {
    if (!periodo?.mes || !periodo?.ano) return "-";
    return `${String(periodo.mes).padStart(2, "0")}/${periodo.ano}`;
}

function formatMoney(value) {
    if (value == null || Number.isNaN(Number(value))) return "-";
    return `R$ ${formatCurrency(value)}`;
}

function buildSearchHaystack(row) {
    return [
        row.conta,
        row.descricao,
        row.debito,
        row.credito,
        row.movimento_mes,
        row.saldo_anterior,
        row.saldo,
        row.conciliacao?.conciliado ? "CONCILIADO" : "",
    ]
        .filter((v) => v != null && String(v).trim() !== "")
        .join(" ")
        .toUpperCase();
}

const Content = () => {
    const api = useApi();
    const tableRef = useRef(null);
    const { openModal, isModalOpen } = useModal();

    const [loading, setLoading] = useState(false);
    const [periodo, setPeriodo] = useState(null);
    const [movimentacoes, setMovimentacoes] = useState([]);
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
    const [q, setQ] = useState("");
    const [linhaSelecionada, setLinhaSelecionada] = useState(null);

    const referenciaAtual = useMemo(() => {
        if (!periodo) return "";
        return `${periodo.ano}-${String(periodo.mes).padStart(2, "0")}`;
    }, [periodo]);

    const fetchMovimentacoes = useCallback(
        async ({ ano, mes }) => {
            setLoading(true);

            try {
                const { data } = await api.get("/Painel/API/Contabilidade/MovimentacaoContas/index.php", {
                    params: { ano, mes: Number(mes) },
                });
                setMovimentacoes(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error(error);
                setMovimentacoes([]);
            } finally {
                setLoading(false);
            }
        },
        [api]
    );

    const handleConfirmPeriodo = useCallback(
        ({ ano, mes }) => {
            const next = { ano: Number(ano), mes: Number(mes) };
            setPeriodo(next);
            fetchMovimentacoes(next);
        },
        [fetchMovimentacoes]
    );

    const handleOpenPeriodoModal = useCallback(() => {
        openModal("selecionarMesContabilidade");
    }, [openModal]);

    const handleRowClick = useCallback(
        (row) => {
            if (!periodo) return;
            setLinhaSelecionada(row);
            openModal("conciliacaoContaContabilidade");
        },
        [openModal, periodo]
    );

    const handleConciliacaoSaved = useCallback(() => {
        if (!periodo) return;
        fetchMovimentacoes(periodo);
    }, [fetchMovimentacoes, periodo]);

    useEffect(() => {
        openModal("selecionarMesContabilidade");
        // Abre apenas na montagem; openModal muda de referência ao fechar o modal.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filtered = useMemo(() => {
        const query = q.trim().toUpperCase();
        if (!query) return movimentacoes;

        return movimentacoes.filter((row) => buildSearchHaystack(row).includes(query));
    }, [movimentacoes, q]);

    const totalConciliadas = useMemo(
        () => movimentacoes.filter((row) => row?.conciliacao?.conciliado).length,
        [movimentacoes]
    );

    useEffect(() => {
        setVisibleCount(PAGE_SIZE);
        if (tableRef.current) {
            tableRef.current.scrollTop = 0;
        }
    }, [filtered]);

    const loadMoreRows = useCallback(() => {
        setVisibleCount((prev) => {
            const total = filtered.length;
            if (prev >= total) return prev;
            return Math.min(prev + PAGE_SIZE, total);
        });
    }, [filtered.length]);

    const columns = useMemo(
        () => [
            {
                id: "status",
                header: "",
                accessor: (row) => (row?.conciliacao?.conciliado ? 1 : 0),
                sortable: true,
                width: "48px",
                align: "center",
                render: (_, row) =>
                    row?.conciliacao?.conciliado ? (
                        <CheckCell title="Conciliado">
                            <FaCheck size={16} />
                        </CheckCell>
                    ) : null,
            },
            {
                id: "conta",
                header: "CONTA",
                accessor: "conta",
                sortable: true,
                width: "140px",
            },
            {
                id: "descricao",
                header: "DESCRIÇÃO",
                accessor: "descricao",
                sortable: true,
            },
            {
                id: "debito",
                header: "DÉBITO",
                accessor: "debito",
                sortable: true,
                align: "right",
                width: "140px",
                render: (value) => formatMoney(value),
            },
            {
                id: "credito",
                header: "CRÉDITO",
                accessor: "credito",
                sortable: true,
                align: "right",
                width: "140px",
                render: (value) => formatMoney(value),
            },
            {
                id: "movimento_mes",
                header: "MOV. MÊS",
                accessor: "movimento_mes",
                sortable: true,
                align: "right",
                width: "140px",
                render: (value) => formatMoney(value),
            },
            {
                id: "saldo_anterior",
                header: "SALDO ANT.",
                accessor: "saldo_anterior",
                sortable: true,
                align: "right",
                width: "140px",
                render: (value) => formatMoney(value),
            },
            {
                id: "saldo",
                header: "SALDO",
                accessor: "saldo",
                sortable: true,
                align: "right",
                width: "140px",
                render: (value) => formatMoney(value),
            },
        ],
        []
    );

    return (
        <PageContent>
            <HeaderBar>
                <Badge>Período: {periodo ? formatPeriodoLabel(periodo) : "Não selecionado"}</Badge>
                <Badge>Total: {loading ? "..." : filtered.length}</Badge>
                <Badge>Conciliadas: {loading ? "..." : totalConciliadas}</Badge>
                <ActionButton type="button" onClick={handleOpenPeriodoModal}>
                    Alterar período
                </ActionButton>
                <FilterInput
                    placeholder="Pesquisar por conta, descrição ou valores..."
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    disabled={!periodo}
                />
            </HeaderBar>

            <TableShell>
                <TableFlexFill>
                    <SortableTable
                        ref={tableRef}
                        columns={columns}
                        data={filtered}
                        getRowId={(row) => row.conta}
                        isLoading={loading}
                        loadingText="Carregando movimentação contábil..."
                        emptyText={
                            periodo
                                ? "NENHUMA MOVIMENTAÇÃO ENCONTRADA"
                                : "SELECIONE UM PERÍODO PARA CONSULTAR"
                        }
                        initialSort={{ key: "conta", direction: "asc" }}
                        limit={visibleCount}
                        onScrollReachEnd={loadMoreRows}
                        scrollReachThreshold={SCROLL_THRESHOLD}
                        onRowClick={periodo ? handleRowClick : undefined}
                        rowCursor={periodo ? "pointer" : "default"}
                    />
                </TableFlexFill>
            </TableShell>

            {isModalOpen("selecionarMesContabilidade") && (
                <ModalSelecionarMes
                    onConfirm={handleConfirmPeriodo}
                    initialReferencia={referenciaAtual}
                />
            )}

            {isModalOpen("conciliacaoContaContabilidade") && linhaSelecionada && periodo && (
                <ModalConciliacaoConta
                    row={linhaSelecionada}
                    periodo={periodo}
                    onSaved={handleConciliacaoSaved}
                />
            )}
        </PageContent>
    );
};

export default Content;
